import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { sortByName } from '../../../utils/sort';
import type { SettingsSiteRow } from '../types';

interface ActiveProject {
  id: string;
  name: string;
  year: number;
}

interface UseManageSitesReturn {
  sites: SettingsSiteRow[];
  archivedSites: SettingsSiteRow[];
  archivedLoaded: boolean;
  loadArchived: () => Promise<void>;
  activeProjects: ActiveProject[];
  addSite: (name: string, projectId: string | null, color: string) => Promise<boolean>;
  updateSite: (id: string, name: string, color: string) => Promise<boolean>;
  archiveSite: (id: string) => Promise<boolean>;
  unarchiveSite: (id: string) => Promise<boolean>;
  deleteSite: (id: string) => Promise<'ok' | 'has_bookings' | 'error'>;
}

export function useManageSites(orgId: string | null): UseManageSitesReturn {
  const [sites, setSites] = useState<SettingsSiteRow[]>([]);
  const [archivedSites, setArchivedSites] = useState<SettingsSiteRow[]>([]);
  const [archivedLoaded, setArchivedLoaded] = useState(false);
  const [activeProjects, setActiveProjects] = useState<ActiveProject[]>([]);
  const projectMapRef = useRef<Record<string, { name: string; year: number }>>({});

  const loadActive = async () => {
    if (!orgId) {
      setSites([]);
      setActiveProjects([]);
      return;
    }
    try {
      const { data: projs } = await supabase
        .from('projects')
        .select('id, name, archived, year')
        .eq('organization_id', orgId)
        .order('year', { ascending: false });

      projectMapRef.current = Object.fromEntries((projs ?? []).map((p) => [p.id, { name: p.name, year: p.year }]));
      setActiveProjects(
        (projs ?? []).filter((p) => !p.archived).map((p) => ({ id: p.id, name: p.name, year: p.year })),
      );

      const { data: sitesData } = await supabase
        .from('sites')
        .select('*')
        .eq('organization_id', orgId)
        .eq('archived', false)
        .order('name');

      const siteIds = (sitesData ?? []).map((s) => s.id);
      const { data: bookingRows } = siteIds.length
        ? await supabase.from('bookings').select('site_id').in('site_id', siteIds)
        : { data: [] };
      const sitesWithBookings = new Set((bookingRows ?? []).map((b) => b.site_id));

      setSites(
        (sitesData ?? []).map((s) => ({
          ...s,
          project_name: s.project_id ? (projectMapRef.current[s.project_id]?.name ?? '') : null,
          project_year: s.project_id ? (projectMapRef.current[s.project_id]?.year ?? null) : null,
          has_bookings: sitesWithBookings.has(s.id),
        })),
      );
    } catch {
      setSites([]);
      setActiveProjects([]);
    }
  };

  const refreshArchived = async (): Promise<boolean> => {
    if (!orgId) return false;
    try {
      const { data: sitesData } = await supabase
        .from('sites')
        .select('*')
        .eq('organization_id', orgId)
        .eq('archived', true)
        .order('name');

      const siteIds = (sitesData ?? []).map((s) => s.id);
      const { data: bookingRows } = siteIds.length
        ? await supabase.from('bookings').select('site_id').in('site_id', siteIds)
        : { data: [] };
      const sitesWithBookings = new Set((bookingRows ?? []).map((b) => b.site_id));

      setArchivedSites(
        (sitesData ?? []).map((s) => ({
          ...s,
          project_name: s.project_id ? (projectMapRef.current[s.project_id]?.name ?? '') : null,
          project_year: s.project_id ? (projectMapRef.current[s.project_id]?.year ?? null) : null,
          has_bookings: sitesWithBookings.has(s.id),
        })),
      );
      return true;
    } catch {
      setArchivedSites([]);
      return false;
    }
  };

  const loadArchived = async () => {
    if (!orgId || archivedLoaded) return;
    setArchivedLoaded(true);
    const ok = await refreshArchived();
    if (!ok) setArchivedLoaded(false);
  };

  useEffect(() => {
    setArchivedLoaded(false);
    setArchivedSites([]);
    loadActive();
  }, [orgId]);

  const addSite = async (name: string, projectId: string | null, color: string): Promise<boolean> => {
    if (!orgId) return false;
    try {
      const { error } = await supabase.from('sites').insert({
        name,
        color,
        organization_id: orgId,
        project_id: projectId ?? null,
        archived: false,
      });
      if (error) throw error;
      await loadActive();
      return true;
    } catch {
      return false;
    }
  };

  const updateSite = async (id: string, name: string, color: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('sites').update({ name, color }).eq('id', id);
      if (error) throw error;
      const update = (list: SettingsSiteRow[]) =>
        sortByName(list.map((s) => (s.id === id ? { ...s, name, color } : s)));
      setSites(update);
      setArchivedSites(update);
      return true;
    } catch {
      return false;
    }
  };

  const archiveSite = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('sites').update({ archived: true }).eq('id', id);
      if (error) throw error;
      setSites((prev) => prev.filter((s) => s.id !== id));
      if (archivedLoaded) await refreshArchived();
      return true;
    } catch { return false; }
  };

  const unarchiveSite = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('sites').update({ archived: false }).eq('id', id);
      if (error) throw error;
      setArchivedSites((prev) => prev.filter((s) => s.id !== id));
      await loadActive();
      return true;
    } catch { return false; }
  };

  const deleteSite = async (id: string): Promise<'ok' | 'has_bookings' | 'error'> => {
    try {
      const { count } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('site_id', id);
      if ((count ?? 0) > 0) return 'has_bookings';
      const { error } = await supabase.from('sites').delete().eq('id', id);
      if (error) throw error;
      setSites((prev) => prev.filter((s) => s.id !== id));
      setArchivedSites((prev) => prev.filter((s) => s.id !== id));
      return 'ok';
    } catch {
      return 'error';
    }
  };

  return {
    sites,
    archivedSites,
    archivedLoaded,
    loadArchived,
    activeProjects,
    addSite,
    updateSite,
    archiveSite,
    unarchiveSite,
    deleteSite,
  };
}
